import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import HelpArticleService from '../../services/helpArticleService';
import HelpRecommendationService from '../../services/helpRecommendationService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ArticleDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [allArticles, setAllArticles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [helpfulVoted, setHelpfulVoted] = useState(false);
  const [unhelpfulVoted, setUnhelpfulVoted] = useState(false);

  // Load article by slug
  useEffect(() => {
    const loadArticle = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await HelpArticleService.getArticleBySlug(slug);
        if (result.success) {
          setArticle(result.data);
          
          // Track view
          try {
            await HelpArticleService.incrementViewCount(result.data.id);
          } catch (err) {
            console.error('Error tracking view:', err);
          }
        } else {
          setError('Article not found');
        }
      } catch (err) {
        console.error('Error loading article:', err);
        setError('Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadArticle();
    }
  }, [slug]);

  // Load all articles for search and related topics
  useEffect(() => {
    const loadAllArticles = async () => {
      try {
        const result = await HelpArticleService.getAllArticles();
        if (result.success) {
          setAllArticles(result.data || []);
        }
      } catch (err) {
        console.error('Error loading articles:', err);
      }
    };

    loadAllArticles();
  }, []);

  // Get related articles based on category and tags
  useEffect(() => {
    if (article && allArticles.length > 0) {
      const related = allArticles.filter(a => {
        // Don't include the current article
        if (a.id === article.id) return false;
        
        // Match by category or tags
        const sameCategory = a.category === article.category;
        const hasCommonTag = article.tags?.some(tag => a.tags?.includes(tag));
        
        return sameCategory || hasCommonTag;
      }).slice(0, 5); // Limit to 5 related articles
      
      setRelatedArticles(related);
    }
  }, [article, allArticles]);

  // Handle search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = allArticles.filter(a => {
      const titleMatch = a.title.toLowerCase().includes(query);
      const descMatch = a.description?.toLowerCase().includes(query);
      const tagsMatch = a.tags?.some(tag => tag.toLowerCase().includes(query));
      
      return titleMatch || descMatch || tagsMatch;
    }).slice(0, 10); // Limit to 10 results

    setSearchResults(results);
    setShowSearchResults(true);
  }, [searchQuery, allArticles]);

  const handleArticleClick = (articleSlug) => {
    navigate(`/help-support/${articleSlug}`);
    setShowSearchResults(false);
    setSearchQuery('');
  };

  const handleHelpful = async (isHelpful) => {
    if (!article) return;

    try {
      await HelpArticleService.voteOnArticle(article.id, isHelpful);
      if (isHelpful) {
        setHelpfulVoted(true);
        setUnhelpfulVoted(false);
      } else {
        setUnhelpfulVoted(true);
        setHelpfulVoted(false);
      }
    } catch (err) {
      console.error('Error voting on article:', err);
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <main className="pt-16 pb-20 md:pb-8">
          <div className="content-container">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-background">
        <main className="pt-16 pb-20 md:pb-8">
          <div className="content-container">
            <div className="bg-white rounded-lg border border-border p-8 max-w-2xl mx-auto text-center">
              <Icon name="AlertCircle" size={48} className="mx-auto text-red-500 mb-4" />
              <h1 className="text-2xl font-bold text-text-primary mb-2">
                {error || 'Article Not Found'}
              </h1>
              <p className="text-text-secondary mb-6">
                The article you're looking for doesn't exist or has been removed.
              </p>
              <Button
                variant="primary"
                onClick={() => navigate('/help-support')}
              >
                Back to Help Center
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16 pb-20 md:pb-8">
        <div className="content-container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search Bar */}
          <div className="relative mb-8">
            <div className="relative">
              <Icon 
                name="Search" 
                size={20} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted pointer-events-none"
              />
              <input
                type="text"
                placeholder="Search help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-white text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Search Results Dropdown */}
            {showSearchResults && searchQuery.trim() && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                {searchResults.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {searchResults.map(result => (
                      <div
                        key={result.id}
                        onClick={() => handleArticleClick(result.slug)}
                        className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <h4 className="font-semibold text-text-primary mb-1">{result.title}</h4>
                        <p className="text-sm text-text-secondary line-clamp-2">
                          {result.description}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded capitalize">
                            {result.category?.replace('-', ' ')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-text-secondary">
                    No articles found matching your search.
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/help-support')}
              className="flex items-center space-x-2 text-primary hover:text-primary-dark mb-6 transition-colors"
            >
              <Icon name="ArrowLeft" size={20} />
              <span>Back to Help Center</span>
            </button>            {/* Thumbnail */}
            {article.thumbnail_url && (
              <div className="relative h-80 bg-gray-100 rounded-lg overflow-hidden mb-6">
                <img
                  src={article.thumbnail_url}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Title and Meta */}
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h1 className="text-4xl font-bold text-text-primary mb-2">
                    {article.title}
                  </h1>
                  <p className="text-lg text-text-secondary">
                    {article.description}
                  </p>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium border whitespace-nowrap ${getUrgencyColor(article.urgency)}`}>
                  {article.urgency}
                </div>
              </div>

              {/* Article Info */}
              <div className="flex flex-wrap gap-6 pt-4 pb-6 border-b border-gray-200">
                <div className="flex items-center space-x-2">
                  <Icon name={article.icon_name || 'HelpCircle'} size={20} className="text-primary" />
                  <div>
                    <p className="text-xs text-text-muted uppercase font-semibold">Category</p>
                    <p className="text-sm font-medium text-text-primary capitalize">
                      {article.category?.replace('-', ' ')}
                    </p>
                  </div>
                </div>

                {article.estimated_time && (
                  <div>
                    <p className="text-xs text-text-muted uppercase font-semibold">Reading Time</p>
                    <p className="text-sm font-medium text-text-primary">{article.estimated_time}</p>
                  </div>
                )}

                <div>
                  <p className="text-xs text-text-muted uppercase font-semibold">Found Helpful</p>
                  <p className="text-sm font-medium text-text-primary">{article.helpful_count || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg border border-border p-8 mb-8">
            {/* Article Content */}
            {article.content && (
              <div className="mb-8 pb-8 border-b border-gray-200 break-words">
                <div
                  className="prose prose-sm max-w-none text-text-secondary leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </div>
            )}

            {/* Steps */}
            {article.steps && article.steps.length > 0 && (
              <div className="mb-8 pb-8 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-text-primary mb-6">Step-by-Step Guide</h2>
                <ol className="space-y-4">
                  {article.steps.map((step, index) => {
                    const stepTitle = typeof step === 'string' ? step : step.title;
                    const stepDesc = typeof step === 'string' ? '' : (step.description || '');
                    return (
                      <li key={index} className="flex space-x-4">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center">
                          {index + 1}
                        </span>
                        <div className="flex-1 pt-1">
                          <span className="text-text-secondary">{stepTitle}</span>
                          {stepDesc && (
                            <p className="text-sm text-text-tertiary mt-1">{stepDesc}</p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="mb-8">
                <h3 className="font-semibold text-text-primary mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map(tag => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Helpful Feedback */}
          <div className="bg-white rounded-lg border border-border p-8 mb-8">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Was this article helpful?</h3>
            <div className="flex items-center space-x-4">
              <Button
                variant={helpfulVoted ? 'primary' : 'outline'}
                iconName="ThumbsUp"
                onClick={() => handleHelpful(true)}
              >
                Yes, it helped
              </Button>
              <Button
                variant={unhelpfulVoted ? 'danger' : 'outline'}
                iconName="ThumbsDown"
                onClick={() => handleHelpful(false)}
              >
                No, not helpful
              </Button>
            </div>
            {(helpfulVoted || unhelpfulVoted) && (
              <p className="text-sm text-text-secondary mt-4">
                Thank you for your feedback!
              </p>
            )}
          </div>

          {/* Back Button */}
          <div className="text-center">
            <Button
              variant="outline"
              iconName="ArrowLeft"
              onClick={() => navigate('/help-support')}
            >
              Back to Help Center
            </Button>
          </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <div className="bg-white rounded-lg border border-border p-6 sticky top-24">
                  <h3 className="text-lg font-bold text-text-primary mb-4 flex items-center space-x-2">
                    <Icon name="Link2" size={20} />
                    <span>Related Topics</span>
                  </h3>
                  
                  <div className="space-y-3">
                    {relatedArticles.map(relatedArticle => (
                      <button
                        key={relatedArticle.id}
                        onClick={() => handleArticleClick(relatedArticle.slug)}
                        className="w-full text-left p-3 rounded-lg hover:bg-gray-50 border border-transparent hover:border-border transition-all group"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-text-primary group-hover:text-primary transition-colors line-clamp-2">
                              {relatedArticle.title}
                            </p>
                            <p className="text-xs text-text-muted mt-1 capitalize">
                              {relatedArticle.category?.replace('-', ' ')}
                            </p>
                          </div>
                          <Icon 
                            name="ArrowRight" 
                            size={16} 
                            className="flex-shrink-0 text-text-muted group-hover:text-primary mt-1 transition-colors"
                          />
                        </div>
                      </button>
                    ))}
                  </div>

                  {relatedArticles.length > 0 && (
                    <button
                      onClick={() => navigate('/help-support')}
                      className="w-full mt-4 py-2 text-sm font-semibold text-primary hover:text-primary-dark transition-colors border-t border-gray-200 pt-4"
                    >
                      View All Articles →
                    </button>
                  )}
                </div>
              )}

              {/* Quick Links */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6 sticky top-[420px]">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center space-x-2">
                  <Icon name="Lightbulb" size={18} />
                  <span>Quick Tips</span>
                </h4>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span>Use the search bar to find specific topics</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span>Share your feedback to help us improve</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span>Check related topics for more information</span>
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ArticleDetailPage;
